import React, { useState, useEffect, useRef } from 'react';
import BlueButton from '../../components/BlueButton';
import { useStateContext } from '../../context/StateContext';
import { useAuthContext } from '../auth/hooks/useAuthContext';
import { AuthContext } from '../../context/AuthContext';
import T, { dT } from '../../components/Translation';
import Swal from 'sweetalert2';
import { GiStarsStack } from "react-icons/gi";
import VisDiv from './VisDiv';
import Loading, { rumiQuotes } from './Loading';

const OpenAiCustomDataJson = ({ 
  systemPrompt = "", 
  model='gpt-4o-mini', 
  maxNTokens = false,
  temperature = 0.7,
  customData = [],
  strict = false,
  aiQuestion, 
  setAiJson,
  dataExists = false,
  jsonSchema,
  alertTitle,
  alertText,
  loadingCallback = () => {}
}) => {

  const { user } = useAuthContext(AuthContext)
  const { apiUrl, language, hideTestButtons, setRawAiConcert } = useStateContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingSectionRef = useRef(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    loadingSectionRef.current?.scrollIntoView({ behavior: 'smooth' });

    if (!aiQuestion) {
      setError('A message is required');
      return;
    }

    setError(null);

    const messageToSend = {
      role: 'user',
      content: aiQuestion
    };
   
    const callOpenAi = async () => {
      setIsLoading(true);
      loadingCallback(true);
      
      try {
        // Construct system prompt with custom data
        const customDataString = (customData.length === 0 || customData) ? "" : `\n\nReference Material:\n${customData.join('\n\n')}.`;
        const strictPrompt = !strict ? "" : `\n\nIMPORTANT: You must ONLY use information from the provided Reference Material above. Do not incorporate any other knowledge or information in your responses. Do not include any raw data structures or arrays in your response.`;
        const finalSystemPrompt = `${systemPrompt}${customDataString}${strictPrompt}`;
        console.log('Sending Data To OpenAI:', {finalSystemPrompt, messageToSend, jsonSchema});
        // Send request to QStash endpoint
        const response = await fetch(`${apiUrl}/api/qstash/openai/process`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model,
            messages: [
              { role: "system", content: finalSystemPrompt },
              messageToSend
            ],
            response_format: jsonSchema ? jsonSchema : { type: "json_object" },
            stream: false,
            max_tokens: maxNTokens ? 4096 : 1024,
            temperature
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to send request to QStash');
        }

        const responseData = await response.json();
        const messageId = responseData.messageId;
        
        process.env.NODE_ENV === "development" && console.log("Received message ID from QStash:", messageId);
        
        // Implement exponential backoff polling strategy with delayed start
        const maxWaitTime = 120000; // 2 minutes maximum wait time
        const initialDelay = 15000; // Wait 15 seconds before first poll
        const maxInterval = 10000; // Cap at 10 seconds per request
        
        let currentInterval = maxInterval; // After initial delay, use reasonable interval
        let totalWaitTime = 0;
        let pollTimeoutId = null;
        
        // Initialize polling variables
        
        const pollStatus = async () => {
          try {
            if (totalWaitTime >= maxWaitTime) {
              setError('Request timed out after 2 minutes. Please try again.');
              setIsLoading(false);
              loadingCallback(false);
              return;
            }

            process.env.NODE_ENV === "development" && console.log(`Polling status for messageId: ${messageId}`);
            const statusUrl = `${apiUrl}/api/qstash/openai/status/${messageId}`;
            process.env.NODE_ENV === "development" && console.log(`Status URL: ${statusUrl}`);
            const statusResponse = await fetch(statusUrl);
            const statusData = await statusResponse.json();

            if (statusData.error) {
              // If it's just "not found", keep polling with backoff
              if (statusData.error === 'Response not found or expired') {
                // Increase interval with exponential backoff
                currentInterval = Math.min(currentInterval * 1.5, maxInterval);
                totalWaitTime += currentInterval;
                pollTimeoutId = setTimeout(pollStatus, currentInterval);
                return;
              }
              // For other errors, stop polling
              setError(`Error: ${statusData.error}`);
              setIsLoading(false);
              loadingCallback(false);
              return;
            }

            if (statusData.completed) {
              // Save the raw response in the StateContext if available
              if (setRawAiConcert) {
                process.env.NODE_ENV === "development" && console.log("Saving raw AI response:", statusData);
                setRawAiConcert(statusData);
              }
              
              if (statusData.response) {
                setAiJson(JSON.stringify(statusData.response));
                setIsLoading(false);
                loadingCallback(false);
              } else {
                setError('Failed to process AI response. Please try again.');
                setIsLoading(false);
                loadingCallback(false);
              }
              return;
            }
            
            // If not completed, continue polling with backoff
            currentInterval = Math.min(currentInterval * 1.5, maxInterval);
            totalWaitTime += currentInterval;
            pollTimeoutId = setTimeout(pollStatus, currentInterval);
            
          } catch (error) {
            setError(`Error polling status: ${error.message}`);
            setIsLoading(false);
            loadingCallback(false);
          }
        };
        
        // Start polling after initial delay
        process.env.NODE_ENV === "development" && console.log(`Waiting ${initialDelay/1000} seconds before first status check...`);
        pollTimeoutId = setTimeout(pollStatus, initialDelay);
        
        // Cleanup polling on component unmount
        return () => {
          if (pollTimeoutId) {
            clearTimeout(pollTimeoutId);
          }
        };
      } catch (error) {
        setError(`Error: ${error.message}`);
        setIsLoading(false);
        loadingCallback(false);
      }
    };
    try {
      if(dataExists) {
        Swal.fire({
          title:  alertTitle,
          text: alertText,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: dT({ en: `YES, PROCEED`, it:`SI', PROSEGUI`}, language),
          cancelButtonText: dT({ en: `NO, CANCEL`, it:`NO, ANNULLA`}, language)
        }).then(async(result) => {
          if (result.isConfirmed) {
            await callOpenAi()
          }
        })
      } else {
        await callOpenAi()
      }

    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error calling OpenAI:', error);
      if (error.status === 401) {
        setError('Invalid API key. Please check your configuration.');
      } else if (error.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else if (error.status === 500) {
        setError('OpenAI API service error. Please try again later.');
      } else if (error.message === 'Invalid response from OpenAI API') {
        setError('Received an invalid response from OpenAI. Please try again.');
      } else {
        setError(`Error: ${error.message || 'An unknown error occurred'}`);
      }
    }
  };

  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <BlueButton 
            buttonStyle={{borderRadius: '8px'}} 
            margins={false} 
            fn={handleSubmit} 
            text=""
            disabled={isLoading || !aiQuestion?.trim()}
          >
            <div style={{cursor: 'pointer', padding: '9px', display: 'flex', alignItems: 'center'}}>
              <GiStarsStack size={25}/>
              <div style={{ marginLeft: '10px', fontSize: '18px' }}>
                <T obj={{"en": "Generate Concert", "it": "Genera Concerto"}}/> 
              </div>
            </div>
          </BlueButton>
        </div>
        <VisDiv rules={[isLoading]}>
          <div ref={loadingSectionRef} style={{ 
              padding: '12px 16px',
              marginTop: '10px',
              marginBottom: '20px',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
              color: '#666',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              <T obj={{en: "AI is creating your concert. Be aware that AI may make mistakes, reMind is not responsible for its content", it: "IA sta creando il tuo Concerto. IA può commettere errori, reMind non è responsabile del suo contenuto"}}/>
            </div>
            <Loading progressBar={true} quotes={rumiQuotes}/>
        </VisDiv>
        {error && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '10px' 
          }}>
            <div style={{ 
              padding: '12px 16px',
              borderRadius: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
              {typeof error === 'string' ? error : error?.message || 'An error occurred'}
            </div>
          </div>
        )}
    </div>
  );
};

export default OpenAiCustomDataJson;

export const buildConcertSchema = (songsData) => {
  // Flatten all lyrics across all songs
  const allLyrics = songsData.flatMap(song =>
    song.lyrics.map(lyric => ({
      id: lyric._id,
      text: lyric.text
    }))
  );

  return {
    type: "json_schema",
    json_schema: {
      name: "make_a_concert",
      schema: {
        type: "object",
        required: ["title", "description", "aiAnswer", "aiQuestion", "questionLanguage", "songs"],
        properties: {
          title: { type: "string", description: "generate a title for the concert based on the theme" },
          description: { type: "string", description: "short description of the concert theme" },
          aiAnswer: { type: "string", description: "your detailed response explaining how the songs and lyrics relate to the user's situation. If the question is in Italian, write this in Italian but keep lyrics in English. If the question is in English, write in English" },
          aiQuestion: { type: "string", description: "exactly the message the user is asking you to answer." },
          questionLanguage: { type: "string", description: "en for English, it for Italian", enum : ["en", "it"] },
          songs: {
            type: "array",
            minItems: 5,
            maxItems: 5,
            items: {
              type: "object",
              required: ["songTemplateId", "title", "affirmation", "aiDescription", "lyrics"],
              properties: {
                songTemplateId: {
                  type: "string",
                  enum: songsData.map(song => song._id),
                  description: "Strictly use the song _id from songsData"
                },
                title: {
                  type: "string",
                  enum: songsData.map(song => song.title),
                  description: "Strictly use the song title from songsData"
                },
                order: { type: "number" },
                affirmation: { type: "string", description: "add a relatable famous quote (without adding the author as it will need to be recited) or create an affirmation related to the song, max 230 characters" },
                aiDescription: { type: "string", description: "briefly explain how this song relates to the user's situation" },
                lyrics: {
                  type: "array",
                  minItems: 4,
                  maxItems: 12,
                  items: {
                    type: "object",
                    required: ["lyricTemplateId", "aiDescription", "text"],
                    properties: {
                      lyricTemplateId: {
                        type: "string",
                        enum: allLyrics.map(l => l?.id),
                        description: "Strictly use lyric _id from songsData"
                      },
                      aiDescription: { type: "string", description: "briefly explain how this lyric relates to the user's situation" },
                      text: {
                        type: "string",
                        enum: allLyrics.map(l => l.text),
                        description: "Exact lyric text from songsData"
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  };
}
